import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, CreditCard, Link2, Copy, Check, Send, Building2, Mail, StickyNote, RefreshCw, XCircle, UserCheck, UserPlus,
} from "lucide-react";
import { api } from "../../lib/api";
import ProposePlanForm from "./ProposePlanForm";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  new: "bg-[#a3823f]/12 text-[#a3823f]",
  priced: "bg-[#4d5a24]/12 text-[#4d5a24]",
  active: "bg-[#1e3d3a] text-white",
  cancelled: "bg-black/8 text-black/40",
};
const STATUS_LABEL = {
  new: "Awaiting price",
  priced: "Link sent",
  active: "Active · paid",
  cancelled: "Cancelled",
};

const PLAN_OPTIONS = [
  { key: "portal_access_monthly", name: "Client Portal Access (self-service)" },
  { key: "reset_monthly", name: "Reset" },
  { key: "momentum_monthly", name: "Momentum" },
  { key: "elevate_monthly", name: "Elevate" },
];

const PlanRequestRow = ({ req, onChanged }) => {
  const [amount, setAmount] = useState(req.custom_amount ? String(req.custom_amount) : "");
  const [billing, setBilling] = useState(req.billing || "monthly");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);

  const changePlan = async (key) => {
    if (!key || key === req.plan_lookup_key) return;
    setPlanBusy(true);
    try {
      const { data } = await api.post(`/admin-auth/subscriptions/${req.id}/plan`, { plan_lookup_key: key });
      toast.success(
        `${req.name || req.email} switched to ${PLAN_OPTIONS.find((p) => p.key === key)?.name}.` +
        (data.client_updated ? " Their portal updated." : ""),
      );
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the plan.");
    } finally { setPlanBusy(false); }
  };

  const sendPrice = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a price greater than $0."); return; }
    setBusy(true);
    try {
      await api.post(`/admin-auth/subscriptions/${req.id}/price`, { amount: amt, billing });
      toast.success(`Payment link sent to ${req.email}.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the payment link.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin-auth/subscriptions/${req.id}/resend`, {});
      toast.success(`Reminder re-sent to ${req.email}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend."); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/admin-auth/subscriptions/${req.id}/cancel`, {});
      toast.message("Request cancelled.");
      onChanged();
    } catch { toast.error("Could not cancel."); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(req.payment_link_url || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const priced = req.status === "priced" || req.status === "active";
  const editable = req.status === "new" || req.status === "priced";

  const toCrm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin-auth/subscriptions/${req.id}/convert`, {});
      toast.success(data.created ? `${req.name} added to the CRM.` : `${req.name} already in the CRM — plan attached.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to CRM."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`subscription-row-${req.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-white p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] font-bold">{req.plan_name}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] ${STATUS_STYLES[req.status] || STATUS_STYLES.new}`}>
              {STATUS_LABEL[req.status] || req.status}
            </span>
            {req.source === "founder" && (
              <span data-testid={`subscription-source-${req.id}`} className="rounded-full border border-[#c9a961]/60 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#a3823f]">
                Sent by {req.proposed_by}
              </span>
            )}
            {req.status === "active" && (
              req.crm_client_id ? (
                <span data-testid={`subscription-crm-${req.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#6b7c3a]/12 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#4d5a24]">
                  <UserCheck className="h-3 w-3" /> In CRM
                </span>
              ) : (
                <button type="button" data-testid={`subscription-to-crm-${req.id}`} onClick={toCrm} disabled={busy}
                  className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-50 transition-colors duration-300">
                  <UserPlus className="h-3 w-3" /> Add to CRM
                </button>
              )
            )}
          </div>
          <p className="mt-1.5 font-medium text-[#1e3d3a]">{req.name}</p>
          <div className="mt-1 flex flex-col gap-0.5 text-[12px] text-[#1e3d3a]/55">
            <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {req.email}</span>
            {req.business_name && <span className="flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {req.business_name}</span>}
            {req.notes && <span className="flex items-start gap-1.5"><StickyNote className="h-3 w-3 mt-0.5" /> <span className="italic">{req.notes}</span></span>}
          </div>
          <div className="mt-2.5 flex items-center gap-2 flex-wrap">
            <label className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">Plan</label>
            <div className="relative inline-flex items-center">
              <select
                data-testid={`subscription-plan-select-${req.id}`}
                value={req.plan_lookup_key || ""}
                disabled={planBusy}
                onChange={(e) => changePlan(e.target.value)}
                className="rounded-full border border-[#1e3d3a]/15 bg-white py-1.5 pl-3 pr-7 text-[11px] text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none disabled:opacity-50"
              >
                {!PLAN_OPTIONS.some((p) => p.key === req.plan_lookup_key) && req.plan_name && (
                  <option value={req.plan_lookup_key || ""}>{req.plan_name}</option>
                )}
                {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
              {planBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#c9a961] ml-1.5" />}
            </div>
            {req.plan_lookup_key === "portal_access_monthly" && (
              <span className="rounded-full bg-[#6b7c3a]/12 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-[#4d5a24]">Self-service</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          {priced && (
            <>
              <p className="font-head text-lg text-[#1e3d3a]">{money(req.custom_amount)}</p>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/45">
                {req.billing === "monthly" ? "per month" : "one-time"}
              </p>
            </>
          )}
          <p className="mt-1 text-[10px] text-[#1e3d3a]/35">{req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}</p>
        </div>
      </div>

      {req.status !== "cancelled" && (
        <div className="mt-3 pt-3 border-t border-[#1e3d3a]/10">
          {editable && (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 mb-1">Amount (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#1e3d3a]/45">$</span>
                  <input
                    type="number" min="1" step="1"
                    data-testid={`subscription-amount-${req.id}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-32 rounded-lg border border-[#1e3d3a]/15 bg-white pl-7 pr-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 mb-1">Billing</label>
                <div className="inline-flex rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/50 p-1">
                  <button
                    type="button"
                    data-testid={`subscription-billing-monthly-${req.id}`}
                    onClick={() => setBilling("monthly")}
                    className={`rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${billing === "monthly" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55"}`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    data-testid={`subscription-billing-onetime-${req.id}`}
                    onClick={() => setBilling("one_time")}
                    className={`rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${billing === "one_time" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55"}`}
                  >
                    One-time
                  </button>
                </div>
              </div>
              <button
                type="button"
                data-testid={`subscription-send-${req.id}`}
                onClick={sendPrice}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.14em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {req.status === "priced" ? "Update & resend" : "Set price & email link"}
              </button>
            </div>
          )}

          {req.payment_link_url && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[11px] text-[#1e3d3a]/50 min-w-0">
                <Link2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate max-w-[280px]">{req.payment_link_url}</span>
              </span>
              <button
                type="button"
                data-testid={`subscription-copy-${req.id}`}
                onClick={copyLink}
                className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
              >
                {copied ? <Check className="h-3 w-3 text-[#4d5a24]" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}
              </button>
              {req.status === "priced" && (
                <button
                  type="button"
                  data-testid={`subscription-resend-${req.id}`}
                  onClick={resend}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-50 transition-colors duration-300"
                >
                  <RefreshCw className="h-3 w-3" /> Resend
                </button>
              )}
            </div>
          )}

          {editable && (
            <button
              type="button"
              data-testid={`subscription-cancel-${req.id}`}
              onClick={cancel}
              disabled={busy}
              className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-black/35 hover:text-[#b23a48] transition-colors duration-300"
            >
              <XCircle className="h-3 w-3" /> Cancel request
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function SubscriptionsCard() {
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("all");
  const [proposing, setProposing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin-auth/subscriptions");
      setRequests(data.requests || []);
    } catch { toast.error("Could not load subscriptions."); setRequests([]); }
  };
  useEffect(() => { load(); }, []);

  if (requests === null) {
    return <div className="py-8 flex items-center justify-center gap-2 text-[#1e3d3a]/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading subscriptions…</div>;
  }

  const counts = {
    all: requests.length,
    new: requests.filter((r) => r.status === "new").length,
    priced: requests.filter((r) => r.status === "priced").length,
    active: requests.filter((r) => r.status === "active").length,
  };
  const shown = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  // Revenue roll-up from active + pending (link sent) requests.
  const active = requests.filter((r) => r.status === "active");
  const priced = requests.filter((r) => r.status === "priced");
  const sumMonthly = (rows) => rows.filter((r) => r.billing === "monthly").reduce((s, r) => s + (Number(r.custom_amount) || 0), 0);
  const sumOneTime = (rows) => rows.filter((r) => r.billing === "one_time").reduce((s, r) => s + (Number(r.custom_amount) || 0), 0);
  const mrr = sumMonthly(active);
  const oneTimeCollected = sumOneTime(active);
  const pendingMrr = sumMonthly(priced);
  const stats = [
    { label: "Monthly recurring", value: money(mrr), sub: `${active.filter((r) => r.billing === "monthly").length} monthly plan${active.filter((r) => r.billing === "monthly").length === 1 ? "" : "s"}`, testid: "subs-stat-mrr", accent: true },
    { label: "Active clients", value: String(active.length), sub: `${counts.priced} awaiting activation`, testid: "subs-stat-active" },
    { label: "One-time collected", value: money(oneTimeCollected), sub: `${active.filter((r) => r.billing === "one_time").length} one-time plan${active.filter((r) => r.billing === "one_time").length === 1 ? "" : "s"}`, testid: "subs-stat-onetime" },
    { label: "Pending MRR", value: money(pendingMrr), sub: "links sent, not yet paid", testid: "subs-stat-pending" },
  ];

  return (
    <div data-testid="subscriptions-card" className="space-y-4">
      <div data-testid="subscriptions-dashboard" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div
            key={s.testid}
            data-testid={s.testid}
            className={`rounded-2xl border p-5 ${s.accent ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/12 bg-white"}`}
          >
            <p className={`text-[10px] uppercase tracking-[0.2em] ${s.accent ? "text-white/55" : "text-[#1e3d3a]/45"}`}>{s.label}</p>
            <p className={`mt-2 font-head text-2xl ${s.accent ? "text-white" : "text-[#1e3d3a]"}`}>{s.value}</p>
            <p className={`mt-1 text-[11px] ${s.accent ? "text-[#c9a961]" : "text-[#1e3d3a]/45"}`}>{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#c9a961]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Subscriptions · custom pricing</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="subscriptions-propose-toggle"
              onClick={() => setProposing((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${proposing ? "border border-[#1e3d3a]/15 text-[#1e3d3a]/55" : "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"}`}
            >
              <Send className="h-3 w-3" /> {proposing ? "Close" : "Send a plan"}
            </button>
            <button
              type="button"
              data-testid="subscriptions-refresh"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
        </div>
        <p className="mt-2 text-sm text-[#1e3d3a]/65 max-w-2xl">
          When a client chooses Reset, Momentum, or Elevate on the site, it lands here without charging them. Set a
          custom price and we'll email them a secure Stripe payment link to activate. Or skip the wait and
          <button type="button" data-testid="subscriptions-propose-inline" onClick={() => setProposing(true)} className="ml-1 underline decoration-[#c9a961] underline-offset-2 hover:text-[#1e3d3a]">send a client the plan you recommend</button>.
        </p>

        {proposing && (
          <div className="mt-4">
            <ProposePlanForm onClose={() => setProposing(false)} onSent={() => { setProposing(false); setFilter("all"); load(); }} />
          </div>
        )}

        <div className="mt-4 flex gap-2 flex-wrap">
          {[
            { k: "all", label: "All" },
            { k: "new", label: "Awaiting price" },
            { k: "priced", label: "Link sent" },
            { k: "active", label: "Active" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              data-testid={`subscriptions-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 ${filter === f.k ? "bg-[#1e3d3a] text-white" : "border border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:text-[#1e3d3a]"}`}
            >
              {f.label} · {counts[f.k]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {shown.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#1e3d3a]/15 bg-[#eae7de]/30 p-8 text-center text-sm text-[#1e3d3a]/45">
            No plan requests {filter !== "all" ? `in "${filter}"` : "yet"}. New requests from the pricing section appear here.
          </div>
        )}
        {shown.map((req) => (
          <PlanRequestRow key={req.id} req={req} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
